import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User, Phone, Mail, MapPin, ShoppingBag, CheckCircle,
    ChevronRight, ChevronLeft, Package, IndianRupee,
    FileText, Plus, Minus, Search, Loader2, X,
    Zap, Shield, BadgePercent, Calculator,
    SlidersHorizontal, CheckCircle2, RefreshCcw, Star,
    Upload, Camera, ImageIcon, Trash2, Home as HomeIcon, Building2, Maximize2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import SubsidyForm from '../components/SubsidyForm';
import { useAuth, API } from '../App';

// PM Surya Ghar subsidy calculation
const calculateSubsidy = (kw) => {
    if (!kw || kw <= 0) return 0;
    if (kw <= 2) return Math.min(kw * 30000, 60000);
    if (kw <= 3) return 60000 + (kw - 2) * 18000;
    return 78000;
};

const STEPS = [
    { id: 1, label: 'Customer Info', icon: User },
    { id: 2, label: 'Select Products', icon: Package },
    { id: 3, label: 'Order Type', icon: FileText },
    { id: 4, label: 'Review & Place', icon: CheckCircle },
];

const SalesmanOrderForm = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    // Products
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productSearch, setProductSearch] = useState('');

    // Filters
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterBrand, setFilterBrand] = useState('all');
    const [filterSubsidy, setFilterSubsidy] = useState(false);

    // Calculator
    const [showCalc, setShowCalc] = useState(false);
    const [calcForm, setCalcForm] = useState({
        monthly_bill: '',
        property_type: 'home',
        city: '',
        backup_required: false,
        roof_area: ''
    });
    const [calcResult, setCalcResult] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [recommendedKw, setRecommendedKw] = useState(null);

    // Cart
    const [cart, setCart] = useState([]);

    // Product Detail Drawer
    const [detailProduct, setDetailProduct] = useState(null);

    // Proof of Work uploads
    const [pow, setPow] = useState({
        delivery_url: null,   // site / installation photo
        bill_url: null,       // customer bill / receipt
        agreement_url: null,  // signed agreement
    });
    const [uploadingPow, setUploadingPow] = useState(null); // key of currently uploading slot

    // Forms
    const [customerForm, setCustomerForm] = useState({
        customer_name: searchParams.get('customerName') || '',
        customer_email: searchParams.get('customerEmail') || '',
        customer_phone: searchParams.get('customerPhone') || '',
        shipping_address: searchParams.get('address') || '',
    });

    const [orderForm, setOrderForm] = useState({
        payment_method: 'cash_collected',
        claim_subsidy: false,
        subsidy_details: {},
    });

    const visitId = searchParams.get('visitId');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => { fetchProducts(); }, []);

    const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
            const res = await axios.get(`${API}/api/products?limit=100`);
            setProducts(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Failed to fetch products');
        } finally {
            setLoadingProducts(false);
        }
    };

    const resolveImg = (url) =>
        url?.startsWith('/uploads') ? `${API}${url}` : (url || 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400');

    // Derived
    const availableBrands = useMemo(() =>
        [...new Set(products.map(p => p.brand).filter(Boolean))].sort(),
        [products]
    );

    const filteredProducts = useMemo(() => {
        let r = products;
        if (filterCategory !== 'all') r = r.filter(p => p.category === filterCategory);
        if (filterBrand !== 'all') r = r.filter(p => p.brand === filterBrand);
        if (filterSubsidy) r = r.filter(p => p.subsidy_eligible);
        if (productSearch) {
            const s = productSearch.toLowerCase();
            r = r.filter(p => p.name?.toLowerCase().includes(s) || p.brand?.toLowerCase().includes(s));
        }
        if (recommendedKw) {
            r = [...r].sort((a, b) =>
                Math.abs((a.system_size_kw || 0) - recommendedKw) - Math.abs((b.system_size_kw || 0) - recommendedKw)
            );
        }
        return r;
    }, [products, filterCategory, filterBrand, filterSubsidy, productSearch, recommendedKw]);

    const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
    const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
    const hasSubsidyItems = cart.some(i => i.subsidy_eligible);

    // Handlers
    const handleCalc = async () => {
        if (!calcForm.monthly_bill || !calcForm.city) {
            toast.error('Please fill in monthly bill and city');
            return;
        }

        setIsCalculating(true);
        try {
            const response = await axios.post(`${API}/api/calculator`, {
                monthly_bill: parseFloat(calcForm.monthly_bill),
                property_type: calcForm.property_type,
                city: calcForm.city,
                backup_required: calcForm.backup_required,
                roof_area: calcForm.roof_area ? parseFloat(calcForm.roof_area) : null
            });
            setCalcResult(response.data);
            setRecommendedKw(response.data.recommended_size_kw);
            toast.success(`Recommended: ~${response.data.recommended_size_kw} kW system`);
        } catch (error) {
            toast.error('Calculation failed.');
        } finally {
            setIsCalculating(false);
        }
    };

    const addToCart = (product) => {
        setCart(prev => {
            const ex = prev.find(i => i.id === product.id);
            if (ex) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
            return [...prev, { ...product, quantity: 1 }];
        });
        toast.success(`${product.name} added`);
    };

    const updateQty = (id, delta) => {
        setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter(i => i.quantity > 0));
    };

    const validate = () => {
        if (step === 1) {
            if (!customerForm.customer_name.trim()) { toast.error('Customer name required'); return false; }
            if (!customerForm.customer_phone.trim()) { toast.error('Customer phone required'); return false; }
            if (!customerForm.shipping_address.trim()) { toast.error('Address required'); return false; }
        }
        if (step === 2 && cart.length === 0) { toast.error('Add at least one product'); return false; }
        return true;
    };

    const nextStep = () => { if (validate()) setStep(s => Math.min(s + 1, 4)); };
    const prevStep = () => setStep(s => Math.max(s - 1, 1));

    // Upload a single PoW file and store the returned URL
    const handlePowUpload = async (slotKey, file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error('File too large — max 10MB'); return; }
        const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowed.includes(file.type)) { toast.error('Only JPG, PNG or PDF accepted'); return; }

        setUploadingPow(slotKey);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/api/upload`, fd, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' }
            });
            const url = res.data.url || res.data.file_url;
            if (!url) { toast.error('Upload failed — no URL returned'); return; }
            setPow(prev => ({ ...prev, [slotKey]: url }));
            toast.success('Document uploaded ✓');
        } catch (e) {
            toast.error('Upload failed');
        } finally {
            setUploadingPow(null);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/salesman/orders`, {
                ...customerForm,
                items: cart.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, system_size_kw: i.system_size_kw, image_url: i.image_url })),
                payment_method: orderForm.payment_method,
                claim_subsidy: orderForm.claim_subsidy,
                subsidy_details: orderForm.claim_subsidy ? orderForm.subsidy_details : null,
                visit_id: visitId || null,
                // Proof of Work — admin reviews and approves/rejects these
                pow_delivery_url: pow.delivery_url || null,
                pow_bill_url: pow.bill_url || null,
                pow_agreement_url: pow.agreement_url || null,
            }, { headers });
            toast.success('Order placed successfully!');
            navigate('/salesman/dashboard');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to place order');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DashboardLayout userRole="salesman">
            <div className="max-w-5xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <ShoppingBag className="w-7 h-7 text-primary" />
                        Place Order for Customer
                    </h1>
                    {visitId && <p className="text-sm text-muted-foreground mt-1">Linked to assignment — customer details pre-filled</p>}
                </div>

                {/* Stepper */}
                <div className="flex items-center gap-0">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.id}>
                            <div className="flex flex-col items-center flex-1">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step > s.id ? 'bg-green-500 text-white' : step === s.id ? 'bg-primary text-white shadow-lg shadow-primary/30' : 'bg-muted text-muted-foreground'}`}>
                                    {step > s.id ? <CheckCircle className="w-5 h-5" /> : s.id}
                                </div>
                                <p className={`text-xs mt-1 font-medium hidden sm:block ${step === s.id ? 'text-primary' : 'text-muted-foreground'}`}>{s.label}</p>
                            </div>
                            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 -mt-5 transition-colors ${step > s.id ? 'bg-green-500' : 'bg-muted'}`} />}
                        </React.Fragment>
                    ))}
                </div>

                {/* ── STEP 1: Customer Info ── */}
                {step === 1 && (
                    <div className="bg-card border rounded-2xl p-6 space-y-5">
                        <h2 className="font-bold text-lg flex items-center gap-2"><User className="w-5 h-5 text-primary" /> Customer Details</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="cname">Full Name *</Label>
                                <Input id="cname" placeholder="Customer full name" value={customerForm.customer_name} onChange={e => setCustomerForm({ ...customerForm, customer_name: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cphone">Phone *</Label>
                                <Input id="cphone" placeholder="10-digit mobile" value={customerForm.customer_phone} onChange={e => setCustomerForm({ ...customerForm, customer_phone: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cemail">Email (optional)</Label>
                                <Input id="cemail" type="email" placeholder="customer@email.com" value={customerForm.customer_email} onChange={e => setCustomerForm({ ...customerForm, customer_email: e.target.value })} />
                            </div>
                            <div className="md:col-span-2 space-y-1.5">
                                <Label htmlFor="caddr">Installation Address *</Label>
                                <textarea id="caddr" className="w-full border rounded-lg px-3 py-2 text-sm bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" rows={3} placeholder="Full address including pincode" value={customerForm.shipping_address} onChange={e => setCustomerForm({ ...customerForm, shipping_address: e.target.value })} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 2: Product Selection ── */}
                {step === 2 && (
                    <div className="space-y-4">
                        {/* Quick Calculator */}
                        <div className="bg-card border rounded-xl overflow-hidden">
                            <button onClick={() => setShowCalc(!showCalc)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/30 transition-colors">
                                <span className="flex items-center gap-2 font-semibold text-sm">
                                    <Calculator className="w-4 h-4 text-primary" />
                                    Quick Solar Calculator
                                    <span className="text-xs text-muted-foreground font-normal hidden sm:inline">(Find right system size)</span>
                                </span>
                                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showCalc ? 'rotate-90' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {showCalc && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                        <div className="px-5 pb-5 pt-4 border-t bg-gradient-to-br from-primary/5 to-orange-500/5">
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs">Monthly Bill (₹) *</Label>
                                                    <Input type="number" placeholder="e.g. 5000" value={calcForm.monthly_bill} onChange={e => setCalcForm({ ...calcForm, monthly_bill: e.target.value })} className="h-9 bg-background" onKeyDown={e => e.key === 'Enter' && handleCalc()} />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs">City *</Label>
                                                    <Input type="text" placeholder="e.g. Delhi" value={calcForm.city} onChange={e => setCalcForm({ ...calcForm, city: e.target.value })} className="h-9 bg-background" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs">Property Type</Label>
                                                    <Select value={calcForm.property_type} onValueChange={(v) => setCalcForm({ ...calcForm, property_type: v })}>
                                                        <SelectTrigger className="h-9 bg-background">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="home"><div className="flex items-center gap-2"><HomeIcon className="w-3.5 h-3.5" /> Home</div></SelectItem>
                                                            <SelectItem value="commercial"><div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Commercial</div></SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs">Roof Area (sq. ft.)</Label>
                                                    <div className="relative">
                                                        <Maximize2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                                        <Input type="number" placeholder="Optional" value={calcForm.roof_area} onChange={e => setCalcForm({ ...calcForm, roof_area: e.target.value })} className="h-9 pl-8 bg-background" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between mt-4 p-3 bg-background rounded-lg border border-primary/10">
                                                <div>
                                                    <Label className="text-sm">Battery Backup</Label>
                                                    <p className="text-[10px] text-muted-foreground">For power outages</p>
                                                </div>
                                                <Switch checked={calcForm.backup_required} onCheckedChange={(c) => setCalcForm({ ...calcForm, backup_required: c })} />
                                            </div>

                                            <div className="mt-4 flex gap-2">
                                                <Button size="sm" onClick={handleCalc} disabled={isCalculating} className="btn-primary flex-1">
                                                    {isCalculating ? 'Calculating...' : 'Calculate Savings'}
                                                </Button>
                                                {recommendedKw && (
                                                    <Button size="sm" variant="outline" onClick={() => { setRecommendedKw(null); setCalcResult(null); setCalcForm({ monthly_bill: '', property_type: 'home', city: '', backup_required: false, roof_area: '' }); }} className="text-muted-foreground">
                                                        <X className="w-3 h-3 m-0" />
                                                    </Button>
                                                )}
                                            </div>

                                            {calcResult && (
                                                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-4 bg-background border rounded-xl space-y-3">
                                                    <div className="flex items-center gap-2 border-b pb-2 mb-2">
                                                        <Zap className="w-4 h-4 text-primary shrink-0" />
                                                        <p className="text-sm font-bold text-primary">Calculation Result</p>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                                        <div>
                                                            <p className="text-xs text-muted-foreground">Recommended Size</p>
                                                            <p className="font-bold text-base">{Math.round(calcResult.recommended_size_kw)} kW</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-muted-foreground">Estimated Cost</p>
                                                            <p className="font-bold text-base">₹{calcResult.estimated_cost?.toLocaleString()}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-muted-foreground">Annual Savings</p>
                                                            <p className="font-bold text-green-600">₹{calcResult.annual_savings?.toLocaleString()}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-muted-foreground">Payback Period</p>
                                                            <p className="font-bold">{calcResult.payback_years} years</p>
                                                        </div>
                                                    </div>
                                                    {calcForm.roof_area && (
                                                        <div className={`mt-2 p-2 rounded text-xs ${parseFloat(calcForm.roof_area) >= calcResult.recommended_size_kw * 100 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                                            {parseFloat(calcForm.roof_area) >= calcResult.recommended_size_kw * 100
                                                                ? `✓ Roof area sufficient (~${calcResult.recommended_size_kw * 100} sq.ft. needed)`
                                                                : `✗ Roof area too small (~${calcResult.recommended_size_kw * 100} sq.ft. needed)`}
                                                        </div>
                                                    )}
                                                    <p className="text-[10px] text-muted-foreground text-center mt-2 italic">Products below are automatically sorted to match ~{calcResult.recommended_size_kw} kW</p>
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Filters */}
                        <div className="bg-card border rounded-xl p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <SlidersHorizontal className="w-4 h-4 text-primary" />
                                <span className="font-semibold text-sm">Filters</span>
                                {(filterCategory !== 'all' || filterBrand !== 'all' || filterSubsidy) && (
                                    <button onClick={() => { setFilterCategory('all'); setFilterBrand('all'); setFilterSubsidy(false); }} className="ml-auto text-xs text-primary hover:underline">Clear All</button>
                                )}
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input placeholder="Search products or brand..." className="pl-9 h-9" value={productSearch} onChange={e => setProductSearch(e.target.value)} />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {[{ key: 'all', label: 'All' }, { key: 'home', label: '🏠 Home' }, { key: 'commercial', label: '🏭 Commercial' }].map(cat => (
                                    <button key={cat.key} onClick={() => setFilterCategory(cat.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterCategory === cat.key ? 'bg-primary text-white shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                                        {cat.label}
                                    </button>
                                ))}
                                {availableBrands.length > 0 && (
                                    <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-all ${filterBrand !== 'all' ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300' : 'bg-muted border-transparent text-muted-foreground'}`}>
                                        <option value="all">All Brands</option>
                                        {availableBrands.map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                )}
                                <button onClick={() => setFilterSubsidy(!filterSubsidy)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterSubsidy ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                                    <BadgePercent className="w-3.5 h-3.5" /> Subsidy Eligible
                                </button>
                            </div>
                        </div>

                        {/* Grid + Sidebar */}
                        <div className="grid lg:grid-cols-3 gap-4">
                            {/* Product Grid */}
                            <div className="lg:col-span-2 space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <p className="text-sm text-muted-foreground">
                                        {loadingProducts ? 'Loading...' : `${filteredProducts.length} products`}
                                        {recommendedKw && <span className="text-primary font-medium ml-1">· sorted for ~{recommendedKw}kW</span>}
                                    </p>
                                    {cartCount > 0 && <span className="text-xs font-semibold text-primary">{cartCount} in cart · ₹{cartTotal.toLocaleString('en-IN')}</span>}
                                </div>

                                {loadingProducts ? (
                                    <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
                                ) : filteredProducts.length === 0 ? (
                                    <div className="text-center py-16 bg-card border rounded-xl text-muted-foreground">
                                        <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                        <p className="font-medium">No products found</p>
                                        <p className="text-sm mt-1">Try adjusting your filters</p>
                                    </div>
                                ) : (
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {filteredProducts.map(p => {
                                            const inCart = cart.find(i => i.id === p.id);
                                            const subsidy = calculateSubsidy(p.system_size_kw);
                                            return (
                                                <div key={p.id} className={`bg-card border rounded-xl overflow-hidden hover:shadow-md transition-all group ${p.subsidy_eligible ? 'ring-1 ring-green-300 dark:ring-green-700' : ''}`}>
                                                    {/* Image */}
                                                    <div className="relative h-40 bg-muted/30 overflow-hidden">
                                                        <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                                                            {p.subsidy_eligible && (
                                                                <span className="flex items-center gap-1 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
                                                                    <BadgePercent className="w-2.5 h-2.5" /> Subsidy
                                                                </span>
                                                            )}
                                                            {p.category && (
                                                                <span className="bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full capitalize">{p.category}</span>
                                                            )}
                                                        </div>
                                                        <button onClick={() => setDetailProduct(p)} className="absolute top-2 right-2 bg-white/90 dark:bg-black/70 text-[11px] font-semibold px-2.5 py-1 rounded-full hover:bg-white dark:hover:bg-black transition-colors shadow-sm opacity-0 group-hover:opacity-100">
                                                            View Details
                                                        </button>
                                                        {/* Always-visible details button on mobile */}
                                                        <button onClick={() => setDetailProduct(p)} className="sm:hidden absolute top-2 right-2 bg-white/90 dark:bg-black/70 text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
                                                            Details
                                                        </button>
                                                    </div>
                                                    {/* Info */}
                                                    <div className="p-3 space-y-2">
                                                        <div>
                                                            <p className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</p>
                                                            <p className="text-xs text-muted-foreground mt-0.5">{p.brand}{p.system_size_kw ? ` · ${p.system_size_kw}kW` : ''}{p.efficiency_rating ? ` · ${p.efficiency_rating}% eff.` : ''}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-base font-bold text-primary">₹{p.price?.toLocaleString('en-IN')}</p>
                                                            {p.subsidy_eligible && subsidy > 0 && (
                                                                <p className="text-[11px] text-green-600 dark:text-green-400 font-medium flex items-center gap-1 mt-0.5">
                                                                    <BadgePercent className="w-3 h-3 shrink-0" />
                                                                    Save ₹{subsidy.toLocaleString('en-IN')} subsidy → eff. ₹{(p.price - subsidy).toLocaleString('en-IN')}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {inCart ? (
                                                            <div className="flex items-center justify-between bg-primary/8 border border-primary/20 rounded-lg p-1.5">
                                                                <button onClick={() => updateQty(p.id, -1)} className="w-8 h-8 rounded-md border flex items-center justify-center hover:bg-muted transition-colors"><Minus className="w-3.5 h-3.5" /></button>
                                                                <span className="font-bold text-sm w-8 text-center">{inCart.quantity}</span>
                                                                <button onClick={() => updateQty(p.id, 1)} className="w-8 h-8 rounded-md border flex items-center justify-center hover:bg-muted transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                                                            </div>
                                                        ) : (
                                                            <Button size="sm" onClick={() => addToCart(p)} className="w-full h-8 btn-primary text-xs">
                                                                <Plus className="w-3.5 h-3.5 mr-1" /> Add to Cart
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Cart Sidebar — desktop */}
                            <div className="hidden lg:block">
                                <div className="bg-card border rounded-xl sticky top-6 overflow-hidden">
                                    <div className="px-4 py-3 border-b flex items-center gap-2 bg-muted/30">
                                        <ShoppingBag className="w-4 h-4 text-primary" />
                                        <span className="font-semibold text-sm">Cart</span>
                                        {cartCount > 0 && <span className="ml-auto bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">{cartCount}</span>}
                                    </div>
                                    {cart.length === 0 ? (
                                        <div className="text-center py-10 text-muted-foreground">
                                            <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-20" />
                                            <p className="text-sm">No products added yet</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="divide-y max-h-72 overflow-y-auto">
                                                {cart.map(i => (
                                                    <div key={i.id} className="px-3 py-2.5 flex items-center gap-2.5">
                                                        <img src={resolveImg(i.image_url)} alt={i.name} className="w-10 h-10 object-cover rounded-lg shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-semibold truncate">{i.name}</p>
                                                            <p className="text-xs text-muted-foreground">₹{i.price?.toLocaleString('en-IN')}</p>
                                                            {i.subsidy_eligible && <p className="text-[10px] text-green-600">Subsidy eligible</p>}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => updateQty(i.id, -1)} className="w-6 h-6 rounded bg-muted flex items-center justify-center hover:bg-red-100 hover:text-red-600 transition-colors"><Minus className="w-3 h-3" /></button>
                                                            <span className="text-xs font-bold w-4 text-center">{i.quantity}</span>
                                                            <button onClick={() => updateQty(i.id, 1)} className="w-6 h-6 rounded bg-muted flex items-center justify-center hover:bg-green-100 hover:text-green-600 transition-colors"><Plus className="w-3 h-3" /></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="px-4 py-3 border-t space-y-1 bg-muted/20">
                                                <div className="flex justify-between font-bold text-sm">
                                                    <span>Total</span>
                                                    <span className="text-primary">₹{cartTotal.toLocaleString('en-IN')}</span>
                                                </div>
                                                {hasSubsidyItems && (
                                                    <p className="text-[11px] text-green-600 flex items-center gap-1">
                                                        <BadgePercent className="w-3 h-3" /> Contains subsidy-eligible products
                                                    </p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Mobile sticky cart bar */}
                        {cart.length > 0 && (
                            <div className="lg:hidden fixed bottom-16 left-0 right-0 bg-card border-t px-4 py-3 flex items-center gap-3 z-30 shadow-2xl safe-area-bottom">
                                <ShoppingBag className="w-5 h-5 text-primary shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm font-bold">{cartCount} item{cartCount > 1 ? 's' : ''} · ₹{cartTotal.toLocaleString('en-IN')}</p>
                                    {hasSubsidyItems && <p className="text-[11px] text-green-600">Includes subsidy-eligible items</p>}
                                </div>
                                <Button onClick={nextStep} className="btn-primary gap-1.5 shrink-0">
                                    Next <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Order Type + Full Subsidy Form ── */}
                {step === 3 && (
                    <div className="space-y-5">
                        <div className="bg-card border rounded-2xl p-6 space-y-5">
                            <h2 className="font-bold text-lg flex items-center gap-2">
                                <FileText className="w-5 h-5 text-primary" /> Order Type & Payment
                            </h2>

                            {/* Subsidy choice cards */}
                            <div className="grid md:grid-cols-2 gap-4">
                                {[
                                    { label: 'Standard Order', desc: 'Regular solar installation without government subsidy', value: false, icon: ShoppingBag, cls: 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/10', ic: 'text-blue-600' },
                                    { label: 'With PM Surya Ghar Subsidy', desc: hasSubsidyItems ? 'Customer eligible for up to ₹78,000 govt subsidy — complete full form below' : 'Fill subsidy form (Note: no subsidy-eligible items in cart currently)', value: true, icon: BadgePercent, cls: 'border-green-300 bg-green-50/50 dark:bg-green-900/10', ic: 'text-green-600' },
                                ].map(opt => (
                                    <button key={String(opt.value)} onClick={() => setOrderForm(prev => ({ ...prev, claim_subsidy: opt.value }))} className={`p-4 rounded-xl border-2 text-left transition-all ${orderForm.claim_subsidy === opt.value ? `${opt.cls} scale-[1.01] shadow-sm` : 'border-border hover:border-muted-foreground/40'}`}>
                                        <opt.icon className={`w-6 h-6 mb-2 ${opt.ic}`} />
                                        <p className="font-bold text-sm">{opt.label}</p>
                                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{opt.desc}</p>
                                    </button>
                                ))}
                            </div>

                            {/* Payment method */}
                            <div className="space-y-2">
                                <Label className="font-semibold">Payment Method</Label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {[{ k: 'cash_collected', l: 'Cash Collected' }, { k: 'upi', l: 'UPI' }, { k: 'bank_transfer', l: 'Bank Transfer' }, { k: 'pending', l: 'Pending' }].map(pm => (
                                        <button key={pm.k} onClick={() => setOrderForm(prev => ({ ...prev, payment_method: pm.k }))} className={`p-3 rounded-lg border text-sm font-medium transition-all ${orderForm.payment_method === pm.k ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-muted-foreground/40'}`}>
                                            {pm.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Full Subsidy Form — reuses existing SubsidyForm component with full document uploads */}
                        <SubsidyForm
                            formData={orderForm}
                            setFormData={setOrderForm}
                            claimSubsidy={orderForm.claim_subsidy}
                            setClaimSubsidy={(val) => setOrderForm(prev => ({ ...prev, claim_subsidy: val }))}
                        />
                    </div>
                )}

                {/* ── STEP 4: Review & Place ── */}
                {step === 4 && (
                    <div className="bg-card border rounded-2xl p-6 space-y-5">
                        <h2 className="font-bold text-lg flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-primary" /> Confirm & Place Order
                        </h2>

                        {/* Customer */}
                        <div className="bg-muted/30 rounded-xl p-4 space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer</p>
                            <div className="text-sm space-y-1">
                                <p className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-primary" /><strong>{customerForm.customer_name}</strong></p>
                                <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-primary" />{customerForm.customer_phone}</p>
                                {customerForm.customer_email && <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-primary" />{customerForm.customer_email}</p>}
                                <p className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />{customerForm.shipping_address}</p>
                            </div>
                        </div>

                        {/* Order Items */}
                        <div className="border rounded-xl overflow-hidden">
                            <div className="bg-muted/50 px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Order Items</div>
                            {cart.map(i => (
                                <div key={i.id} className="flex items-center gap-3 px-4 py-3 border-t">
                                    <img src={resolveImg(i.image_url)} alt={i.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{i.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            Qty: {i.quantity} · {i.system_size_kw}kW
                                            {i.subsidy_eligible && <span className="ml-1 text-green-600 font-medium">· Subsidy Eligible</span>}
                                        </p>
                                    </div>
                                    <p className="font-bold text-sm shrink-0">₹{(i.price * i.quantity).toLocaleString('en-IN')}</p>
                                </div>
                            ))}
                            <div className="flex justify-between px-4 py-3 border-t bg-muted/20 font-bold">
                                <span>Total</span>
                                <span className="text-primary text-lg">₹{cartTotal.toLocaleString('en-IN')}</span>
                            </div>
                        </div>

                        {/* Order Details */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-muted/30 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground">Payment Method</p>
                                <p className="font-semibold capitalize">{orderForm.payment_method.replace(/_/g, ' ')}</p>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground">Subsidy Claim</p>
                                <p className={`font-semibold ${orderForm.claim_subsidy ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {orderForm.claim_subsidy ? '✓ PM Surya Ghar' : 'No Subsidy'}
                                </p>
                            </div>
                        </div>

                        {/* Subsidy document status */}
                        {orderForm.claim_subsidy && (() => {
                            const sd = orderForm.subsidy_details || {};
                            const required = ['aadhaar_doc', 'pan_doc', 'electricity_bill_doc', 'bank_passbook_doc'];
                            const uploaded = required.filter(d => sd[d]).length;
                            const ok = uploaded === required.length;
                            return (
                                <div className={`rounded-xl p-4 border ${ok ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800'}`}>
                                    <p className={`text-xs font-semibold mb-1 ${ok ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                        Subsidy Documents: {uploaded}/{required.length} required uploaded
                                    </p>
                                    <p className={`text-xs ${ok ? 'text-green-600' : 'text-amber-600'}`}>
                                        {ok ? '✓ All required documents uploaded — ready to submit' : '⚠ Go back to Step 3 and upload all required documents'}
                                    </p>
                                    <div className="grid grid-cols-2 gap-1 mt-2">
                                        {[
                                            { key: 'aadhaar_doc', label: 'Aadhaar Card' },
                                            { key: 'pan_doc', label: 'PAN Card' },
                                            { key: 'electricity_bill_doc', label: 'Electricity Bill' },
                                            { key: 'bank_passbook_doc', label: 'Bank Passbook' },
                                        ].map(doc => (
                                            <p key={doc.key} className={`text-[11px] flex items-center gap-1 ${sd[doc.key] ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                {sd[doc.key] ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 border rounded-full inline-block" />}
                                                {doc.label}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* ── PROOF OF WORK (Step 4 only) ── */}
                {step === 4 && (
                    <div className="bg-card border rounded-2xl overflow-hidden">
                        {/* Header */}
                        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/10 to-orange-500/10 flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center shrink-0">
                                <Camera className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h2 className="font-bold text-base">Proof of Work</h2>
                                <p className="text-xs text-muted-foreground">Upload evidence — the admin will review and approve/reject before closing the order</p>
                            </div>
                            {/* Upload count badge */}
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${Object.values(pow).filter(Boolean).length > 0 ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                                {Object.values(pow).filter(Boolean).length}/3 uploaded
                            </span>
                        </div>

                        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                { key: 'delivery_url', label: '1. Site / Installation Photo', hint: 'Photo of installed panels at customer site', required: true },
                                { key: 'bill_url', label: '2. Customer Bill / Receipt', hint: 'Copy of bill or payment receipt given to customer', required: false },
                                { key: 'agreement_url', label: '3. Signed Agreement', hint: 'Customer\'s signed installation agreement', required: false },
                            ].map(slot => {
                                const uploaded = pow[slot.key];
                                const isUploading = uploadingPow === slot.key;
                                const resolvedUrl = uploaded?.startsWith('/uploads') ? `${API}${uploaded}` : uploaded;

                                return (
                                    <div key={slot.key} className={`relative border-2 rounded-xl overflow-hidden transition-all ${uploaded ? 'border-green-400 dark:border-green-600' : 'border-dashed border-border'}`}>
                                        {/* Image preview */}
                                        {uploaded && (
                                            <div className="relative h-36 bg-muted">
                                                {uploaded.endsWith('.pdf') ? (
                                                    <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 dark:bg-red-950/20 gap-2">
                                                        <FileText className="w-10 h-10 text-red-500" />
                                                        <span className="text-xs text-red-600 font-medium">PDF uploaded</span>
                                                    </div>
                                                ) : (
                                                    <img src={resolvedUrl} alt={slot.label} className="w-full h-full object-cover" />
                                                )}
                                                {/* Remove button */}
                                                <button
                                                    onClick={() => setPow(prev => ({ ...prev, [slot.key]: null }))}
                                                    className="absolute top-2 right-2 w-6 h-6 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                                {/* View full link */}
                                                {!uploaded.endsWith('.pdf') && (
                                                    <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full hover:bg-black transition-colors">
                                                        Full View
                                                    </a>
                                                )}
                                            </div>
                                        )}

                                        {/* Upload area */}
                                        <label className={`flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${uploaded ? 'p-3' : 'h-36 hover:bg-muted/30'}`}>
                                            {isUploading ? (
                                                <div className="flex flex-col items-center gap-2 py-4">
                                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                                    <span className="text-xs text-muted-foreground">Uploading...</span>
                                                </div>
                                            ) : uploaded ? (
                                                <div className="flex items-center gap-2 w-full">
                                                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                                    <span className="text-xs text-green-600 font-medium flex-1">Uploaded ✓</span>
                                                    <span className="text-[10px] text-muted-foreground">Tap to replace</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                                                        <Upload className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <span className="text-xs font-medium text-center leading-tight">{slot.label.replace(/^\d+\. /, '')}</span>
                                                    <span className="text-[10px] text-muted-foreground text-center leading-tight px-2">{slot.hint}</span>
                                                    <span className="text-[10px] text-primary/70">JPG · PNG · PDF (max 10MB)</span>
                                                </>
                                            )}
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept=".jpg,.jpeg,.png,.pdf"
                                                disabled={isUploading}
                                                onChange={e => { handlePowUpload(slot.key, e.target.files[0]); e.target.value = ''; }}
                                            />
                                        </label>

                                        {/* Label at bottom */}
                                        <div className="px-3 py-2 border-t bg-muted/20 flex items-center gap-1.5">
                                            <span className="text-xs font-semibold text-muted-foreground flex-1">{slot.label}</span>
                                            {slot.required && <span className="text-[10px] text-orange-500 font-medium">Recommended</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Info note */}
                        <div className="mx-5 mb-5 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
                            <ImageIcon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                                These documents will be reviewed by the admin after order placement. Uploading clear photos helps speed up order approval. You can still place the order without uploading — and the salesman can also upload proof later from the order detail screen.
                            </p>
                        </div>
                    </div>
                )}

                {/* Navigation Buttons */}

                <div className={`flex justify-between ${step === 2 && cart.length > 0 ? 'pb-32 lg:pb-0' : ''}`}>
                    <Button variant="outline" onClick={step === 1 ? () => navigate(-1) : prevStep} className="gap-2">
                        <ChevronLeft className="w-4 h-4" />
                        {step === 1 ? 'Cancel' : 'Back'}
                    </Button>
                    {step < 4 ? (
                        <Button onClick={nextStep} className="btn-primary gap-2">
                            Next <ChevronRight className="w-4 h-4" />
                        </Button>
                    ) : (
                        <Button onClick={handleSubmit} disabled={submitting} className="btn-primary gap-2">
                            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Placing...</> : <><CheckCircle className="w-4 h-4" /> Place Order</>}
                        </Button>
                    )}
                </div>
            </div>

            {/* ── Product Detail Drawer ── */}
            <AnimatePresence>
                {detailProduct && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]" onClick={() => setDetailProduct(null)} />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 26, stiffness: 220 }}
                            className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-background border-l shadow-2xl z-[70] flex flex-col"
                        >
                            {/* Drawer Header */}
                            <div className="flex items-center gap-3 px-5 py-4 border-b shrink-0">
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold leading-tight line-clamp-1">{detailProduct.name}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{detailProduct.brand} · {detailProduct.category}</p>
                                </div>
                                <button onClick={() => setDetailProduct(null)} className="p-2 hover:bg-muted rounded-lg transition-colors shrink-0">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Drawer Body */}
                            <div className="flex-1 overflow-y-auto">
                                {/* Image */}
                                <div className="relative h-56 bg-muted/30">
                                    <img src={resolveImg(detailProduct.image_url)} alt={detailProduct.name} className="w-full h-full object-cover" />
                                    <div className="absolute top-3 left-3 flex gap-1.5">
                                        {detailProduct.subsidy_eligible && (
                                            <span className="flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
                                                <BadgePercent className="w-3 h-3" /> Subsidy Eligible
                                            </span>
                                        )}
                                        {detailProduct.category && (
                                            <span className="bg-black/60 text-white text-xs px-2.5 py-1 rounded-full capitalize">{detailProduct.category}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-5 space-y-5">
                                    {/* Price block */}
                                    <div>
                                        <div className="flex items-baseline gap-3">
                                            <span className="text-2xl font-bold text-primary">₹{detailProduct.price?.toLocaleString('en-IN')}</span>
                                            {detailProduct.original_price && (
                                                <span className="text-base text-muted-foreground line-through">₹{detailProduct.original_price?.toLocaleString('en-IN')}</span>
                                            )}
                                        </div>
                                        {detailProduct.subsidy_eligible && (() => {
                                            const sub = calculateSubsidy(detailProduct.system_size_kw);
                                            return sub > 0 ? (
                                                <div className="mt-2 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <p className="text-xs text-green-600 font-medium">Govt. Subsidy Benefit</p>
                                                            <p className="text-lg font-bold text-green-700">−₹{sub.toLocaleString('en-IN')}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-xs text-green-600 font-medium">Effective Price</p>
                                                            <p className="text-xl font-bold text-green-800">₹{(detailProduct.price - sub).toLocaleString('en-IN')}</p>
                                                        </div>
                                                    </div>
                                                    <p className="text-[11px] text-green-600/80 mt-1.5">*Subsidy credited to customer's bank after installation</p>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>

                                    {/* Quick specs */}
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { icon: Zap, label: 'System Size', val: detailProduct.system_size_kw ? `${detailProduct.system_size_kw} kW` : '—' },
                                            { icon: RefreshCcw, label: 'Efficiency', val: detailProduct.efficiency_rating ? `${detailProduct.efficiency_rating}%` : '—' },
                                            { icon: Shield, label: 'Warranty', val: detailProduct.warranty_years ? `${detailProduct.warranty_years} Yrs` : '—' },
                                        ].map((s, i) => (
                                            <div key={i} className="bg-muted/40 rounded-xl p-3 text-center">
                                                <s.icon className="w-4 h-4 text-primary mx-auto mb-1" />
                                                <p className="text-xs font-bold">{s.val}</p>
                                                <p className="text-[10px] text-muted-foreground">{s.label}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Short description */}
                                    {detailProduct.short_description && (
                                        <p className="text-sm text-muted-foreground leading-relaxed">{detailProduct.short_description}</p>
                                    )}

                                    {/* Key Features */}
                                    {detailProduct.key_features?.length > 0 && (
                                        <div className="space-y-2">
                                            <p className="font-semibold text-sm">Key Features</p>
                                            <ul className="space-y-1.5">
                                                {detailProduct.key_features.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm">
                                                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Specifications Table */}
                                    {detailProduct.specifications?.length > 0 && (
                                        <div className="space-y-2">
                                            <p className="font-semibold text-sm">Specifications</p>
                                            <div className="border rounded-xl overflow-hidden">
                                                <table className="w-full text-sm">
                                                    <tbody>
                                                        {detailProduct.specifications.map((spec, i) => (
                                                            <tr key={i} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                                                                <td className="px-4 py-2.5 text-muted-foreground font-medium w-1/2">{spec.label}</td>
                                                                <td className="px-4 py-2.5 font-semibold">{spec.value}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Rating */}
                                    {detailProduct.rating && (
                                        <div className="flex items-center gap-2">
                                            <div className="flex">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star key={i} className={`w-4 h-4 ${i < Math.floor(detailProduct.rating) ? 'fill-primary text-primary' : 'text-muted'}`} />
                                                ))}
                                            </div>
                                            <span className="text-sm font-semibold">{detailProduct.rating}</span>
                                            {detailProduct.review_count && <span className="text-xs text-muted-foreground">({detailProduct.review_count} reviews)</span>}
                                        </div>
                                    )}

                                    {/* Long description */}
                                    {detailProduct.long_description && (
                                        <div className="space-y-2">
                                            <p className="font-semibold text-sm">Description</p>
                                            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{detailProduct.long_description.slice(0, 500)}{detailProduct.long_description.length > 500 ? '...' : ''}</p>
                                        </div>
                                    )}

                                    {/* Spacer for sticky footer */}
                                    <div className="h-20" />
                                </div>
                            </div>

                            {/* Drawer Footer — sticky add-to-cart */}
                            <div className="border-t px-5 py-4 bg-background shrink-0">
                                {(() => {
                                    const inCart = cart.find(i => i.id === detailProduct.id);
                                    return inCart ? (
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2 border rounded-xl px-3 py-2 flex-1 justify-between">
                                                <button onClick={() => updateQty(detailProduct.id, -1)} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-red-100 hover:text-red-600 transition-colors"><Minus className="w-4 h-4" /></button>
                                                <span className="font-bold">{inCart.quantity} in cart</span>
                                                <button onClick={() => updateQty(detailProduct.id, 1)} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-green-100 hover:text-green-600 transition-colors"><Plus className="w-4 h-4" /></button>
                                            </div>
                                            <Button onClick={() => setDetailProduct(null)} className="btn-primary gap-1.5 shrink-0">Done</Button>
                                        </div>
                                    ) : (
                                        <Button className="w-full btn-primary gap-2" onClick={() => { addToCart(detailProduct); }}>
                                            <Plus className="w-4 h-4" /> Add to Cart · ₹{detailProduct.price?.toLocaleString('en-IN')}
                                        </Button>
                                    );
                                })()}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </DashboardLayout>
    );
};

export default SalesmanOrderForm;
